/* Harper & Stone — predictive search.
 *
 * The form submits without JS. This adds live results on top, split between
 * products and guides, because a shopper searching "clarity" wants the
 * education page and one searching "oval" wants the grid.
 */

const MIN_QUERY = 2;
const DEBOUNCE = 220;
const RECENT_KEY = 'hs:recent-searches';
const RECENT_MAX = 5;

class HsSearch extends HTMLElement {
  #controller = null;
  #timer = null;

  connectedCallback() {
    this.dialog = this.querySelector('dialog');
    this.input = this.querySelector('[data-search-input]');
    this.form = this.querySelector('[data-search-form]');
    this.clear = this.querySelector('[data-search-clear]');
    this.resting = this.querySelector('[data-search-resting]');
    this.results = this.querySelector('[data-search-results]');
    this.empty = this.querySelector('[data-search-empty]');
    this.emptyText = this.querySelector('[data-search-empty-text]');
    this.status = this.querySelector('[data-search-status]');
    this.productList = this.querySelector('[data-search-products]');
    this.guideList = this.querySelector('[data-search-guides]');
    this.viewAll = this.querySelector('[data-search-all]');
    this.tabs = [...this.querySelectorAll('[data-search-tab]')];
    this.moneyFormat = this.dataset.moneyFormat || '${{amount}}';
    if (!this.dialog) return;

    for (const opener of document.querySelectorAll('[data-search-open]')) {
      opener.addEventListener('click', (e) => {
        // The drawer's entry is a real link to /search so it works without
        // JS; with JS it opens the overlay instead of leaving the page.
        e.preventDefault();
        this.open();
      });
    }
    for (const close of this.querySelectorAll('[data-search-close]')) {
      close.addEventListener('click', () => this.dialog.close());
    }

    this.input?.addEventListener('input', () => this.#onInput());
    this.clear?.addEventListener('click', () => this.#reset());

    for (const tab of this.tabs) {
      tab.addEventListener('click', () => this.#selectTab(tab.dataset.searchTab));
      // Arrow keys move between tabs, as the tab pattern requires.
      tab.addEventListener('keydown', (e) => {
        const i = this.tabs.indexOf(tab);
        if (e.key === 'ArrowRight') this.tabs[(i + 1) % this.tabs.length].focus();
        if (e.key === 'ArrowLeft') this.tabs[(i - 1 + this.tabs.length) % this.tabs.length].focus();
      });
    }

    this.recent = this.querySelector('[data-search-recent]');
    this.recentList = this.querySelector('[data-search-recent-list]');
    this.#renderRecent();

    // A submitted query is one worth remembering; a previewed one is not.
    this.form?.addEventListener('submit', () => {
      const q = this.input?.value.trim();
      if (q) this.#remember(q);
    });

    this.dialog.addEventListener('close', () => {
      document.body.style.removeProperty('overflow');
      this.#reset();
    });
  }

  disconnectedCallback() {
    this.#controller?.abort();
    clearTimeout(this.#timer);
  }

  open() {
    this.dialog.showModal();
    document.body.style.overflow = 'hidden';
    // A frame, so the dialog is painted before focus moves — otherwise iOS
    // opens the keyboard against a zero-height element.
    requestAnimationFrame(() => this.input?.focus());
  }

  /* Recent searches live in this browser only. Nothing is sent anywhere and
   * nothing is tied to a customer — it is a convenience, not a profile. */
  #readRecent() {
    try {
      return JSON.parse(localStorage.getItem(RECENT_KEY) || '[]');
    } catch (e) {
      return [];
    }
  }

  #remember(q) {
    try {
      const list = [q, ...this.#readRecent().filter((x) => x !== q)].slice(0, RECENT_MAX);
      localStorage.setItem(RECENT_KEY, JSON.stringify(list));
    } catch (e) {
      /* private browsing; the feature simply does not persist */
    }
  }

  #forget(q) {
    try {
      localStorage.setItem(RECENT_KEY, JSON.stringify(this.#readRecent().filter((x) => x !== q)));
    } catch (e) {
      /* nothing to do */
    }
    this.#renderRecent();
  }

  #renderRecent() {
    if (!this.recent || !this.recentList) return;
    const list = this.#readRecent();
    this.recent.hidden = list.length === 0;
    if (!list.length) return;

    this.recentList.innerHTML = list
      .map(
        (q) => `<li><span class="search__chip search__chip--recent">
          <button type="button" class="search__chip-term" data-recent-term>${this.#esc(q)}</button>
          <button type="button" class="search__chip-x" data-recent-remove aria-label="Remove ${this.#esc(q)}">
            <svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="m6 6 12 12M18 6 6 18" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/></svg>
          </button>
        </span></li>`
      )
      .join('');

    for (const el of this.recentList.querySelectorAll('[data-recent-term]')) {
      el.addEventListener('click', () => {
        this.input.value = el.textContent.trim();
        this.input.focus();
        this.#onInput();
      });
    }
    for (const el of this.recentList.querySelectorAll('[data-recent-remove]')) {
      el.addEventListener('click', () => {
        this.#forget(el.closest('.search__chip').querySelector('[data-recent-term]').textContent.trim());
      });
    }
  }

  #reset() {
    if (this.input) this.input.value = '';
    this.clear.hidden = true;
    this.resting.hidden = false;
    this.results.hidden = true;
    this.empty.hidden = true;
    this.#renderRecent();
    this.input?.focus();
  }

  #onInput() {
    const q = this.input.value.trim();
    this.clear.hidden = q.length === 0;

    clearTimeout(this.#timer);
    if (q.length < MIN_QUERY) {
      this.resting.hidden = false;
      this.results.hidden = true;
      this.empty.hidden = true;
      return;
    }
    this.#timer = setTimeout(() => this.#fetch(q), DEBOUNCE);
  }

  async #fetch(q) {
    // Abort the previous request: typing fast otherwise races, and an older
    // response can land after a newer one and show the wrong results.
    this.#controller?.abort();
    this.#controller = new AbortController();

    const url = new URL(window.Shopify?.routes?.root || '/', location.origin);
    url.pathname = `${url.pathname.replace(/\/$/, '')}/search/suggest.json`;
    url.searchParams.set('q', q);
    url.searchParams.set('resources[type]', 'product,page,article');
    url.searchParams.set('resources[limit]', '8');

    try {
      const res = await fetch(url, { signal: this.#controller.signal });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      this.#render(q, data.resources?.results || {});
    } catch (e) {
      if (e.name !== 'AbortError') this.#showEmpty(q);
    }
  }

  #render(q, r) {
    const products = r.products || [];
    const guides = [...(r.pages || []), ...(r.articles || [])];

    if (!products.length && !guides.length) return this.#showEmpty(q);

    this.resting.hidden = true;
    this.empty.hidden = true;
    this.results.hidden = false;

    this.productList.innerHTML = products.map((p) => this.#card(p)).join('');
    this.guideList.innerHTML = guides.map((g) => this.#guide(g)).join('');

    this.querySelector('[data-count-products]').textContent = products.length ? `(${products.length})` : '';
    this.querySelector('[data-count-guides]').textContent = guides.length ? `(${guides.length})` : '';

    // Land on whichever side actually has something.
    this.#selectTab(products.length ? 'products' : 'guides');

    for (const tab of this.tabs) {
      tab.hidden = tab.dataset.searchTab === 'guides' && guides.length === 0;
    }

    if (this.viewAll) {
      this.viewAll.href = `${window.Shopify?.routes?.search_url || '/search'}?q=${encodeURIComponent(q)}`;
    }
    if (this.status) this.status.textContent = (this.dataset.resultsStatus || '[count] results').replace('[count]', products.length + guides.length);
  }

  #showEmpty(q) {
    this.resting.hidden = true;
    this.results.hidden = true;
    this.empty.hidden = false;
    // Never a dead end: say what to try instead.
    this.emptyText.textContent = (this.dataset.noResults || 'Nothing matched [query]. Try a shape, a stone or a style.').replace('[query]', `“${q}”`);
    if (this.status) this.status.textContent = this.dataset.noResultsStatus || 'No results';
  }

  #selectTab(name) {
    for (const tab of this.tabs) {
      const on = tab.dataset.searchTab === name;
      tab.setAttribute('aria-selected', String(on));
      tab.tabIndex = on ? 0 : -1;
      const panel = this.querySelector(`[data-search-panel="${tab.dataset.searchTab}"]`);
      if (panel) panel.hidden = !on;
    }
  }

  /* Shopify's money_format is a template like "${{amount}}" or
   * "Rs. {{amount_with_comma_separator}}". The endpoint hands back a plain
   * decimal, so the shop's own format is applied here rather than shipping a
   * bare number next to a piece of jewellery. */
  #money(value) {
    const n = Number(value);
    if (!isFinite(n)) return String(value ?? '');

    const group = (v, sep, dec, places) => {
      const fixed = Math.abs(v).toFixed(places);
      const [i, f] = fixed.split('.');
      const grouped = i.replace(/\B(?=(\d{3})+(?!\d))/g, sep);
      return places ? `${grouped}${dec}${f}` : grouped;
    };

    return this.moneyFormat.replace(/\{\{\s*(\w+)\s*\}\}/g, (_, token) => {
      switch (token) {
        case 'amount':
          return group(n, ',', '.', 2);
        case 'amount_no_decimals':
          return group(n, ',', '.', 0);
        case 'amount_with_comma_separator':
          return group(n, '.', ',', 2);
        case 'amount_no_decimals_with_comma_separator':
          return group(n, '.', ',', 0);
        case 'amount_with_space_separator':
          return group(n, ' ', ',', 2);
        case 'amount_no_decimals_with_space_separator':
          return group(n, ' ', ',', 0);
        case 'amount_with_apostrophe_separator':
          return group(n, "'", '.', 2);
        default:
          return group(n, ',', '.', 2);
      }
    });
  }

  #esc(s) {
    return String(s ?? '').replace(/[&<>"']/g, (c) => `&#${c.charCodeAt(0)};`);
  }

  #card(p) {
    const img = p.featured_image?.url
      ? `<img src="${this.#esc(p.featured_image.url)}&width=400" alt="${this.#esc(p.title)}" loading="lazy" width="400" height="500">`
      : '';
    return `<article class="pcard">
      <a class="pcard__link" href="${this.#esc(p.url)}">
        <div class="media media--cover media--portrait pcard__media">${img}</div>
      </a>
      <div class="pcard__body">
        <h3 class="pcard__title"><a class="pcard__title-link" href="${this.#esc(p.url)}" tabindex="-1">${this.#esc(p.title)}</a></h3>
        <p class="pcard__price numeric">${this.#esc(this.#money(p.price))}</p>
      </div>
    </article>`;
  }

  #guide(g) {
    return `<li><a class="search__guide" href="${this.#esc(g.url)}">
      <span>${this.#esc(g.title)}</span>
      <svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M9 5.5 15.5 12 9 18.5" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"/></svg>
    </a></li>`;
  }
}

if (!customElements.get('hs-search')) customElements.define('hs-search', HsSearch);
